import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { featuresResponse, type Features } from '@/api/schemas';
import { ApiError, requestParsed } from '@/api/http';

/** Everything off: what an API from before phase 1 supports, and what is assumed while the answer is unknown. */
export const noFeatures: Features = { closeResults: false, addResults: false, priceSnapshot: false, futuresPriceFeed: false };

/**
 * GET /Features. Only a 404 means "everything off": an API from before phase 1 has no such route. Every other failure
 * (no network, timeout, 5xx, an answer of the wrong shape) is thrown, so the query is not cached as "off": it keeps the
 * last good answer, if any, and asks again on the next fetch.
 */
export async function fetchFeatures(): Promise<Features> {
  try {
    return await requestParsed(featuresResponse, '/Features', { auth: false });
  } catch (error) {
    if (error instanceof ApiError && error.kind === 'http' && error.status === 404) return noFeatures;
    throw error;
  }
}

/** The one ['features'] query: the hook and code outside React share its cache entry and its 5-minute freshness. */
export const featuresQuery = queryOptions({ queryKey: ['features'] as const, queryFn: fetchFeatures, staleTime: 5 * 60_000 });

/**
 * The features for code outside React (the price feed): the cached answer while fresh, otherwise a new request. When
 * that request fails, the last good answer is used if there is one; otherwise the error is thrown and nothing is cached,
 * so the next call asks again.
 */
export async function loadFeatures(client: QueryClient): Promise<Features> {
  try {
    return await client.fetchQuery(featuresQuery);
  } catch (error) {
    const known = client.getQueryData(featuresQuery.queryKey);
    if (known) return known;
    throw error;
  }
}

/**
 * What the API supports. Until it has answered, every feature counts as off. A failed refetch keeps the last good answer
 * (TanStack Query keeps a query's data when a later fetch fails).
 */
export function useFeatures(): Features {
  const query = useQuery(featuresQuery);
  return query.data ?? noFeatures;
}
